'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import type { Provider } from '@supabase/supabase-js';
import type { z } from 'zod';

import { getSafeRedirectPath, isBrowser } from '@kit/shared/utils';
import { If } from '@kit/ui/if';
import { Separator } from '@kit/ui/separator';
import { Trans } from '@kit/ui/trans';

import type { PasswordSignInSchema } from '../schemas/password-sign-in.schema';
import { LastAuthMethodHint } from './last-auth-method-hint';
import { MagicLinkAuthContainer } from './magic-link-auth-container';
import { OauthProviders } from './oauth-providers';
import { OtpSignInContainer } from './otp-sign-in-container';
import { PasswordSignInContainer } from './password-sign-in-container';

export function SignInMethodsContainer(props: {
  paths: {
    callback: string;
    joinTeam: string;
    returnPath: string;
  };

  providers: {
    password: boolean;
    magicLink: boolean;
    otp: boolean;
    oAuth: Provider[];
  };

  defaultValues?: Partial<z.infer<typeof PasswordSignInSchema>>;
}) {
  const router = useRouter();

  const returnPath = getSafeRedirectPath(props.paths.returnPath, '/home');
  const callbackUrl = isBrowser()
    ? new URL(props.paths.callback, window.location.origin)
    : null;
  callbackUrl?.searchParams.set('next', returnPath);
  const redirectUrl = callbackUrl?.toString() ?? '';

  const onSignIn = useCallback(() => {
    router.replace(returnPath);
  }, [returnPath, router]);

  return (
    <>
      <LastAuthMethodHint />

      <If condition={props.providers.password}>
        <PasswordSignInContainer
          onSignIn={onSignIn}
          defaultValues={props.defaultValues}
          magicLinkEnabled={props.providers.magicLink}
          magicLinkRedirectUrl={redirectUrl}
        />
      </If>

      {/* When password is enabled the magic-link flow lives inside the
          password form's toggle, so only render the standalone magic-link
          form when password sign-in is turned off. */}
      <If condition={props.providers.magicLink && !props.providers.password}>
        <MagicLinkAuthContainer
          redirectUrl={redirectUrl}
          shouldCreateUser={false}
        />
      </If>

      <If condition={props.providers.otp}>
        <OtpSignInContainer shouldCreateUser={false} />
      </If>

      <If condition={props.providers.oAuth.length}>
        <div className="relative my-2">
          <div className="absolute inset-0 flex items-center">
            <Separator />
          </div>

          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background text-muted-foreground px-2">
              <Trans i18nKey="auth:orContinueWith" />
            </span>
          </div>
        </div>

        <OauthProviders
          enabledProviders={props.providers.oAuth}
          shouldCreateUser={false}
          paths={{
            callback: props.paths.callback,
            returnPath: props.paths.returnPath,
          }}
        />
      </If>
    </>
  );
}
